import type { QueryObserverResult } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';
import React, { type PropsWithChildren, createContext, useMemo } from 'react';

import type { FeatureName } from '@sine/sine-core-api-types';
import { type GlobalFeatureName } from '@sine/sine-core-api-types';
import { useTeamSwitcher } from '@sine/sine-team-switcher';

import { QueryKeys } from '../../query.client';
import { type SiteFeatureNameValue } from '../../team/feature/constants/siteFeatures';
import featureService from '../../team/feature/feature.service';

type TeamFeaturesV2Response = {
  admin: Array<`${FeatureName}`>;
  global: Array<`${GlobalFeatureName}`>;
  site: Array<{
    siteId: string;
    features: Array<`${SiteFeatureNameValue}`>;
  }>;
};
export interface FeaturesContextType {
  adminFeatures: TeamFeaturesV2Response['admin'];
  siteFeatures: TeamFeaturesV2Response['site'];
  globalFeatures: TeamFeaturesV2Response['global'];
  refetchFeatures: () => Promise<QueryObserverResult<TeamFeaturesV2Response, any> | undefined>;
  isLoading: boolean;
  isFetching: boolean;
}

const defaultFeaturesContext: FeaturesContextType = {
  adminFeatures: [],
  siteFeatures: [],
  globalFeatures: [],
  refetchFeatures: () => Promise.resolve(undefined),
  isLoading: true,
  isFetching: false,
};

export const FeaturesContext = createContext(defaultFeaturesContext);

export default function FeaturesProvider({ children }: PropsWithChildren<unknown>) {
  const { currentTeam } = useTeamSwitcher();

  const currentTeamId = currentTeam?.id;

  const { data, isLoading, refetch, isFetching } = useQuery<TeamFeaturesV2Response>({
    enabled: !!currentTeamId,
    queryKey: [QueryKeys.TEAM_FEATURES, currentTeamId],
    queryFn: () => featureService.getEnabledTeamFeatures(currentTeamId),
  });

  const contextValue = useMemo(() => {
    if (!data) {
      return {
        isLoading: true,
        isFetching: true,
        adminFeatures: [],
        siteFeatures: [],
        globalFeatures: [],
        refetchFeatures: () => Promise.resolve(undefined),
      };
    }

    return {
      isLoading,
      isFetching,
      refetchFeatures: refetch,
      adminFeatures: data.admin,
      siteFeatures: data.site,
      globalFeatures: data.global,
    };
  }, [data, isLoading, refetch, isFetching]);

  return <FeaturesContext.Provider value={contextValue}>{children}</FeaturesContext.Provider>;
}
